/**
 * Lift2Fit — admin: Accounts (`#/admin/accounts`).
 *
 * Search accounts by email or name, filter by role, and read each row's plan,
 * created date, last activity and — for a business — seat usage. Paginated with
 * the API's opaque cursor; the search and the filter are server-side, so the
 * result is the platform's answer, not a client-side slice.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type AdminAccountView, type Role } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, ArrowRightIcon, ShieldIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { formatDateShort } from '../crm/shared';
import { Link } from '../router';
import { ADMIN_EYEBROW, coachPlanLabel, formatCount, roleLabel, seatLimitLabel, tierLabel } from './shared';

const ROLE_OPTIONS: ReadonlyArray<{ value: '' | Role; label: string }> = [
  { value: '', label: 'All roles' },
  { value: 'lifter', label: 'Lifters' },
  { value: 'coach', label: 'Coaches & businesses' },
  { value: 'admin', label: 'Admins' },
];

export function AdminAccountsScreen() {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'' | Role>('');
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState<AdminAccountView[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreBusy, setMoreBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const loadFirstPage = useCallback(async () => {
    const request = requestRef.current + 1;
    requestRef.current = request;
    setLoading(true);
    setError(null);
    const result = await api.adminAccounts({
      q: search.trim() === '' ? null : search.trim(),
      role: role === '' ? null : role,
      limit: pageSize,
    });
    if (requestRef.current !== request) return;
    if (result.problem || !result.data) {
      setRows([]);
      setTotal(0);
      setNextCursor(null);
      setError(result.problem?.detail ?? result.problem?.title ?? 'The accounts list did not load.');
      setLoading(false);
      return;
    }
    setRows(result.data.data);
    setTotal(result.data.total);
    setNextCursor(result.data.nextCursor);
    setLoading(false);
  }, [role, search, pageSize]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = async () => {
    if (nextCursor === null) return;
    setMoreBusy(true);
    const result = await api.adminAccounts({
      q: search.trim() === '' ? null : search.trim(),
      role: role === '' ? null : role,
      limit: pageSize,
      cursor: nextCursor,
    });
    setMoreBusy(false);
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? result.problem?.title ?? 'The next page did not load.');
      return;
    }
    const page = result.data;
    setRows((current) => [...current, ...page.data]);
    setNextCursor(page.nextCursor);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="admin-accounts">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Accounts"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Every account on the platform. Search by email or name, filter by role, and open one to change its plan or roles — deliberately and audited."
        actions={
          <AppLinkButton to="/admin/businesses" variant="outline">
            Businesses
          </AppLinkButton>
        }
      />

      <Panel
        title="All accounts"
        subtitle="Server-side search and role filter; the count is the whole matching set."
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Search</span>
              <input
                type="search"
                data-testid="admin-accounts-search"
                value={search}
                placeholder="you@lift2fit.com"
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 w-56 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Role</span>
              <select
                data-testid="admin-accounts-role"
                value={role}
                onChange={(event) => setRole(event.target.value as '' | Role)}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Rows</span>
              <select
                data-testid="admin-accounts-limit"
                value={String(pageSize)}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                {[5, 10, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      >
        <p className="text-[12px] text-ink-muted" data-testid="admin-accounts-total">
          {loading ? 'Searching…' : `${formatCount(total)} account(s) match`}
        </p>

        {error ? (
          <p role="alert" data-testid="admin-accounts-error" className="border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger">
            {error}
          </p>
        ) : null}

        {loading && rows.length === 0 ? (
          <LoadingPanel label="Loading accounts" rows={5} />
        ) : rows.length === 0 && !error ? (
          <EmptyState
            title="No account matches that search"
            body="Try a different email or name, or clear the role filter. Every account that exists is listed here."
            icon={<ShieldIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Account</th>
                  <th className="py-2 pr-3 font-bold">Roles</th>
                  <th className="py-2 pr-3 font-bold">Plan</th>
                  <th className="py-2 pr-3 font-bold">Seats</th>
                  <th className="py-2 pr-3 font-bold">Created</th>
                  <th className="py-2 pr-3 font-bold">Last activity</th>
                  <th className="py-2 font-bold">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((account) => (
                  <tr
                    key={account.userId}
                    data-testid={`admin-account-row-${account.userId}`}
                    className="border-b border-hairline last:border-b-0 align-top"
                  >
                    <td className="py-3 pr-3">
                      <span className="flex min-w-0 flex-col">
                        <span className="text-[13px] font-bold text-ink">{account.displayName}</span>
                        <span className="text-[12px] text-ink-muted">{account.email}</span>
                        <span className="text-[11px] text-ink-muted">
                          {tierLabel(account.tier)}
                          {account.premiumUntil ? ` · until ${formatDateShort(account.premiumUntil)}` : ''}
                        </span>
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      <span className="flex flex-wrap gap-1">
                        {account.roles.map((entry) => (
                          <StatusChip key={entry} tone={entry === 'admin' ? 'danger' : entry === 'coach' ? 'accent' : 'neutral'}>
                            {roleLabel(entry)}
                          </StatusChip>
                        ))}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink">{coachPlanLabel(account.coachPlan)}</td>
                    <td className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-ink-muted">
                      {account.seats === null
                        ? '—'
                        : `${formatCount(account.seats.used)} / ${seatLimitLabel(account.seats.limit)}`}
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">{formatDate(account.createdAt)}</td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">
                      {account.lastActivityAt ? formatDate(account.lastActivityAt) : 'never'}
                    </td>
                    <td className="py-3">
                      <Link
                        to={`/admin/accounts/${account.userId}`}
                        data-testid={`admin-account-open-${account.userId}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover"
                      >
                        Open
                        <ArrowRightIcon />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextCursor !== null ? (
          <button
            type="button"
            data-testid="admin-accounts-more"
            disabled={moreBusy}
            onClick={() => void loadMore()}
            className="inline-flex h-9 w-fit items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
          >
            {moreBusy ? 'Loading…' : 'Load more'}
          </button>
        ) : null}
      </Panel>

      {!loading && rows.length > 0 ? (
        <p className="text-[12px] text-ink-muted">
          Showing {formatCount(rows.length)} of {formatCount(total)} matching account(s).{' '}
          <Link to="/admin" className="text-accent hover:text-accent-hover">
            Back to the overview
          </Link>
          <AlertIcon className="ml-1 inline text-[12px]" />
        </p>
      ) : null}
    </div>
  );
}
